// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import {
  black,
  gray01,
  gray02,
  gray04,
  gray05,
  gray06,
  primary,
  white,
  whiteFix,
} from 'presentations/views/styles/color';
import Icon from 'presentations/views/components/atoms/Icon';
import {
  spacing,
  fontSize,
  fontWeight,
  borderRadius,
  isMediaMax,
} from 'presentations/views/styles';
import { SyntheticEvent, useContext, useMemo } from 'react';
import PlanUpgradeDialog from 'presentations/views/components/organisms/PlanUpgradeDialog';
import Popover from 'presentations/views/components/atoms/Popover';
import Link from 'presentations/views/components/atoms/Link';
import {
  accountSettingProfilePath,
  calendarIntegrationsPath,
  hostEventTypeSettingPath,
} from 'presentations/routes/path';
import { useRouter } from 'next/router';
import { ViewerItem } from 'presentations/api/schema/user';
import Tooltip from 'presentations/views/components/atoms/Tooltip';
import Radio from 'presentations/views/components/atoms/Radio';
import { useDashboardCalendarSettingMenu } from './hooks';
import {
  ShowRange,
  CalendarWeekDay,
} from 'presentations/views/components/atoms/Calendar/reducer';
import { Role } from 'domains/models/team_user/role';
import Checkbox from 'presentations/views/components/atoms/Checkbox';
import Select, { Option } from 'presentations/views/components/atoms/Select';
import { flexibleStartWeekDay } from 'domains/models/event_type/startWeekDay';
import CalendarViewTypeField from 'presentations/views/components/molecules/CalendarViewTypeField';

interface Props {
  anchor: HTMLElement | null;
  viewer: ViewerItem;
  onClose?: () => void;
  showRange: ShowRange;

  onChangeView: (view: number) => void;
  startWeekday: CalendarWeekDay;
  isShowSameEventMerge: boolean;
  isShowEventOverlap: boolean;
  isShowDeclinedEvent: boolean;
  isShowWeekends: boolean;
  timeFormat: string;
  onChangeStartWeekday: (startWeekday: CalendarWeekDay) => void;
  onChangeIsShowSameEventMerge: (isShowSameEventMerge: boolean) => void;
  onChangeIsShowEventOverlap: (isShowEventOverlap: boolean) => void;
  onChangeIsShowDeclinedEvent: (isShowDeclinedEvent: boolean) => void;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();
  const { state, actions } = useDashboardCalendarSettingMenu({
    viewer: props.viewer,
  });
  const isMobile = isMediaMax('tablet');

  const onChange = (
    e: SyntheticEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.currentTarget;
    switch (name) {
      case 'startWeekday':
        props.onChangeStartWeekday(Number(value) as CalendarWeekDay);
        break;
      default:
        break;
    }
  };

  const onChangeView = (e: SyntheticEvent<HTMLDivElement>) => {
    const { view } = e.currentTarget.dataset;
    props.onChangeView(Number(view));
  };

  const onChangeStartWeekday = (e: SyntheticEvent<HTMLDivElement>) => {
    const { wday } = e.currentTarget.dataset;
    props.onChangeStartWeekday(Number(wday) as CalendarWeekDay);
  };

  const onChangeIsEventOverlap = (e: SyntheticEvent<HTMLDivElement>) => {
    props.onChangeIsShowEventOverlap(!props.isShowEventOverlap);
  };

  const onChangeIsEventMerge = (e: SyntheticEvent<HTMLDivElement>) => {
    props.onChangeIsShowSameEventMerge(!props.isShowSameEventMerge);
  };

  const onChangeIsShowDecliendEvent = (e: SyntheticEvent<HTMLDivElement>) => {
    props.onChangeIsShowDeclinedEvent(!props.isShowDeclinedEvent);
  };

  const onSelectCustomView = (e: React.ChangeEvent<{ value: string }>) => {
    e.preventDefault();
    if (!e.currentTarget.value) return;
    props.onChangeView(Number(e.currentTarget.value));
  };

  const options = useMemo(() => {
    const options = [];
    for (let index = 2; index <= 6; index++) {
      options.push({
        value: index,
        text: t('Days', { days: index }),
      });
    }
    return options;
  }, []);

  return (
    <>
      <StyledPopover
        onClose={props.onClose}
        anchor={props.anchor}
        maxHeight={isMobile ? 400 : undefined}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        isKeepMounted={false}
      >
        {isMobile && (
          <Section>
            <SectionTitle>{t('Create Type')}</SectionTitle>
            <ListItem>
              <CalendarViewTypeField />
            </ListItem>
          </Section>
        )}
        <Section>
          <SectionTitle>{t('Term')}</SectionTitle>
          <ListItem>
            <TermMenuListItem
              isActive={props.showRange.day === 1}
              data-view={1}
              onClick={onChangeView}
            >
              <Inner>
                <MenuListItemIcon name="view-day-outline" />
                <MenuListItemTitle>{t('Day')}</MenuListItemTitle>
              </Inner>
            </TermMenuListItem>
            <TermMenuListItem
              isActive={props.showRange.day === 7}
              data-view={7}
              onClick={onChangeView}
            >
              <Inner>
                <MenuListItemIcon name="calendar_view_week" />
                <MenuListItemTitle>{t('Week')}</MenuListItemTitle>
              </Inner>
            </TermMenuListItem>
            <TermMenuListItem
              isActive={props.showRange.month === 1}
              data-view={28}
              onClick={onChangeView}
            >
              <Inner>
                <MenuListItemIcon name="calendar-month" />
                <MenuListItemTitle>{t('Month')}</MenuListItemTitle>
              </Inner>
            </TermMenuListItem>
            <TermMenuListItem
              isActive={props.showRange.day === 4}
              data-view={4}
              onClick={onChangeView}
            >
              <Inner>
                <MenuListItemIcon name="view-week-outline" />
                <MenuListItemTitle>{t('4Day')}</MenuListItemTitle>
              </Inner>
            </TermMenuListItem>
          </ListItem>
          {props.showRange.day &&
            props.showRange.day >= 2 &&
            props.showRange.day <= 6 && (
              <ListItem>
                <FormRow>
                  <FormRowTitle>{t('Show Days')}</FormRowTitle>
                  <StyledSelect
                    value={props.showRange.day}
                    onChange={onSelectCustomView}
                    options={options}
                    variant="standard"
                    size="small"
                    isFullWidth
                  />
                </FormRow>
              </ListItem>
            )}
        </Section>
        <Section>
          <SectionTitle>{t('Start Weekday')}</SectionTitle>
          <ListItem>
            <Row>
              <Radio
                name="startWeekday"
                isChecked={[0, 1, 2, 3, 4, 5, 6].includes(props.startWeekday)}
                label={t('Basic')}
                onChange={onChange}
                value={0}
              />
              <WeekDayList>
                {[0, 1, 2, 3, 4, 5, 6].map((wday) => (
                  <WeekDayListItem
                    key={wday}
                    data-wday={wday}
                    onClick={onChangeStartWeekday}
                    isActive={props.startWeekday === wday}
                  >
                    {t(`week day ${wday}`)}
                  </WeekDayListItem>
                ))}
              </WeekDayList>
              <Radio
                name="startWeekday"
                isChecked={Number(props.startWeekday) === flexibleStartWeekDay}
                label={t('Flexible')}
                onChange={onChange}
                value={flexibleStartWeekDay}
              />
            </Row>
          </ListItem>
        </Section>
        <Section>
          <SectionTitle>{t('Event Position')}</SectionTitle>
          <CheckListItem>
            <Checkbox
              value={props.isShowSameEventMerge ? 1 : 0}
              onChange={onChangeIsEventMerge}
              isChecked={props.isShowSameEventMerge}
              label={t('Event Mergeable label')}
            />
            <Checkbox
              value={props.isShowEventOverlap ? 1 : 0}
              onChange={onChangeIsEventOverlap}
              isChecked={props.isShowEventOverlap}
              label={t('Event Overlap label')}
            />
            <Checkbox
              value={props.isShowDeclinedEvent ? 1 : 0}
              onChange={onChangeIsShowDecliendEvent}
              isChecked={props.isShowDeclinedEvent}
              label={t('Decliend Event label')}
            />
          </CheckListItem>
        </Section>
        <MenuList>
          {props.viewer.role <= Role.Member && (
            <MenuListItemLink
              href={hostEventTypeSettingPath({
                from: encodeURIComponent(router.asPath),
              })}
            >
              <MenuIcon name="calendar-expand-horizontal" />
              <MenuName>{t('Availability')}</MenuName>
            </MenuListItemLink>
          )}
          <MenuListItemLink
            href={calendarIntegrationsPath({
              from: encodeURIComponent(router.asPath),
            })}
          >
            <MenuIcon name="calendar-clock" />
            <MenuName>{t('Calendar Connections')}</MenuName>
          </MenuListItemLink>
          <MenuListItemLink href={accountSettingProfilePath()}>
            <MenuIcon name="settings" />
            <MenuName>{t('Account Settings')}</MenuName>
          </MenuListItemLink>
        </MenuList>
      </StyledPopover>
      {state.isShowingPlanUpgrade && (
        <PlanUpgradeDialog onClose={actions.onClosePlanUpgrade} />
      )}
    </>
  );
}

const MenuListItem = styled.div<{ isActive?: boolean }>`
  /* flex: 1; */
  cursor: pointer;
  border: 1px solid ${(props) => (props.isActive ? primary : gray04)};
  background-color: ${white};
  padding: ${spacing(4)} 0;
  min-width: 96px;
  height: 73px;
  margin-right: ${spacing(12)};
  border-radius: ${borderRadius(6)};
  color: ${(props) => (props.isActive ? primary : black)};
  :hover {
    color: ${primary};
    border-color: ${primary};
  }
  :last-of-type {
    margin-right: 0;
  }
`;
const TermMenuListItem = styled(MenuListItem)`
  min-width: 70px;
`;
const Inner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;
const MenuListItemIcon = styled(Icon)`
  margin-top: ${spacing(8)};
  width: 28px !important;
  color: ${primary};
`;
const MenuListItemTitle = styled.div`
  font-size: ${fontSize(13)};
  margin-top: ${spacing(4)};
  font-weight: ${fontWeight('bold')};
`;
const Section = styled.div`
  width: 343px;
  padding: ${spacing(16)};
  border-bottom: 1px solid ${gray05};
  :last-of-type {
    border-bottom: 0;
  }
`;
const MenuList = styled.div`
  margin-bottom: ${spacing(8)};
`;
const Content = styled.div`
  margin-bottom: ${spacing(16)};
  :last-of-type {
    margin-bottom: 0;
  }
`;
const SectionTitle = styled.div`
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(8)};
  display: flex;
  align-items: center;
`;
const SectionTooltip = styled(Tooltip)`
  display: flex;
  align-items: center;
  margin-left: ${spacing(4)};
`;
const CheckListItem = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${spacing(8)};
  :last-of-type {
    margin-bottom: 0;
  }
`;
const ListItem = styled.div`
  display: flex;
  margin-bottom: ${spacing(8)};
  :last-of-type {
    margin-bottom: 0;
  }
`;
const Row = styled.div``;
const StyledPopover = styled(Popover)``;

const InfoIcon = styled(Icon)`
  width: 18px !important;
  width: 18px !important;
  color: ${gray02};
`;

const DurationContainer = styled.div`
  width: 100%;
`;
const DurationField = styled.div`
  margin-bottom: ${spacing(16)};

  &:last-of-type {
    margin-bottom: 0;
  }
`;
const DurationFieldTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(8)};
`;

const MenuListItemLink = styled(Link)`
  display: flex;
  width: 100%;
  align-items: center;
  padding: ${spacing(8)} ${spacing(16)};
  :last-of-type {
    margin-bottom: 0;
  }
  :hover {
    background-color: ${gray06};
  }
`;
const MenuIcon = styled(Icon)`
  color: ${gray01};
  width: 18px !important;
  height: 18px !important;
  margin-right: ${spacing(12)};
`;
const MenuName = styled.div`
  color: ${gray01};
  font-weight: ${fontWeight('bold')};
`;

const WeekDayList = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(16)};
  margin-left: ${spacing(8)};
`;
const WeekDayListItem = styled.div<{ isActive: boolean }>`
  cursor: pointer;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${(props) => (props.isActive ? primary : gray06)};
  color: ${(props) => (props.isActive ? whiteFix : black)};
  margin-right: ${spacing(4)};
  font-size: ${fontSize(11)};
  :last-of-type {
    margin-right: 0;
  }
`;

const StyledSelect = styled(Select)`
  margin-top: ${spacing(8)};
  width: 100%;
`;
const FormRow = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`;

const FormRowTitle = styled.div`
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
  display: flex;
  align-items: center;
`;
